import { Col, Form, Select } from "antd";
import { useEffect, useRef, useState } from "react";
import {
  EXISTING_PO_MAP_LIST,
  STYLE_MERCHANDISING_LIST,
} from "../../../apiServices/API_ENDPOINTS";
import { getData } from "../../../apiServices/common";
import { isArrayAndHasValue } from "utils/functions";

const BuyerPackingReportFilterFields = ({ initialTableData }) => {
  // States
  const [poList, setPoList] = useState(null);
  const [styleList, setStyleList] = useState([]);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);

  // Antd
  const { Option } = Select;
  const form = Form.useFormInstance();

  const mountedRef = useRef(true);
  const reqIdRef = useRef(0);
  const didSeedDefaultRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const getPOList = async (value = null) => {
    const myReq = ++reqIdRef.current;
    let query = `${EXISTING_PO_MAP_LIST}`;
    if (value) {
      query = `${query}?po_map_id=${value}`;
    }
    const response = await getData(query);

    if (!mountedRef.current || myReq !== reqIdRef.current) return;

    if (response && response?.data?.code === 200) {
      setPoList(response?.data?.data);
    }
    setHasLoadedOnce(true);
  };

  const getStyleList = async (filterValues) => {
    const bodyData = {
      per_page: filterValues?.per_page || 20,
      style_no: filterValues?.style_no || "",
    };
    const response = await getData(STYLE_MERCHANDISING_LIST, false, bodyData);

    if (response && response?.data?.code === 200) {
      setStyleList(response?.data?.data?.data || []);
    }
  };

  // Round 2 (B, R3): StyleController@index has no filter-by-id param (out of this
  // task's allow-list to add one), so the PO's own default style may not be among the
  // generic first-20 styles fetched above. Fetch it individually (existing
  // /{id}/edit route, same pattern CostSheet/index.js already uses for contracts) so
  // the Select can show its label instead of a blank/raw-id option.
  const ensureStyleOption = async (styleId) => {
    if (!styleId) return;
    const response = await getData(`${STYLE_MERCHANDISING_LIST}/${styleId}/edit`);
    if (!mountedRef.current) return;
    const style = response?.data?.data?.[0]?.style;
    if (style?.id) {
      setStyleList((prev) =>
        prev.some((item) => item.id === style.id) ? prev : [style, ...prev],
      );
    }
  };

  useEffect(() => {
    getPOList();
    getStyleList();
  }, []);

  // Round 2 (B, R3): PackingListController@existPoList now returns this list ordered
  // by purchase_order_maps.updated_at desc (was effectively unordered), so the first
  // row is already the most recently updated PO with packing requirements; it also now
  // carries the PO map's own style_id. Set both fields and submit the form once — the
  // same "Generate" path a manual selection uses (exportPacking renders the HTML
  // preview inline; it is not a destructive/export action despite the prop name).
  useEffect(() => {
    if (didSeedDefaultRef.current) return;
    if (!hasLoadedOnce) return;
    didSeedDefaultRef.current = true;
    if (!isArrayAndHasValue(poList)) return;

    const mostRecent = poList[0];
    if (!mostRecent?.id) return;

    form?.setFieldsValue({
      po_map_id: mostRecent.id,
      style_id: mostRecent.style_id,
    });
    ensureStyleOption(mostRecent.style_id).finally(() => {
      if (mountedRef.current) form?.submit();
    });
  }, [hasLoadedOnce, poList, form]);

  return (
    <>
      <Col span={4}>
        <Form.Item
          label="Original PO"
          name="po_map_id"
          rules={[
            {
              required: true,
              message: "Original PO is required",
            },
          ]}
        >
          <Select
            showSearch
            style={{
              width: "100%",
            }}
            placeholder="Search to Select"
            optionFilterProp="children"
            size="small"
            onSearch={getPOList}
            notFoundContent={
              hasLoadedOnce ? "No purchase orders with packing requirements found." : undefined
            }
          >
            {poList &&
              poList?.map((po, idx) => (
                <Option key={idx} value={po?.id}>
                  {po?.original_po}
                </Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
      <Col span={4}>
        <Form.Item label="Style No." name="style_id">
          <Select
            showSearch
            placeholder="Select a Style"
            optionFilterProp="children"
            size="small"
            onSearch={(value) => getStyleList({ style_no: value })}
          >
            {isArrayAndHasValue(styleList) &&
              styleList.map((sup) => (
                <Option value={sup.id} key={sup.id}>
                  {sup.style_no}
                </Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
    </>
  );
};

export default BuyerPackingReportFilterFields;
